<template lang="pug">
  v-layout(row wrap)
    v-form.mr-2(
      ref="form"
      v-model="valid"
      lazy-validation
    )
      .mt-4
        p.title-step.ml-3
          strong What is a campaign?
          |  A campaign represents a use case, including texts sent out immediately, scheduled texts
          | and texts in a campaign. This informs the carrier the type of content you are sending to your contacts.
      v-flex.fee-text(v-if="isStandardRegistration" v-show="!isRetryState" xs12 my-0)
        p.ml-4.mb-2 Unlocking texting requires a registration fee by the US mobile carriers - 
          a(href="https://lonewolf.my.site.com/s/article/LD-FAQ-Texting-brand-registration-changes-June-2023-LionDesk" target="_blank") click here to learn more:
        ul.fee-list.ml-4
          li One-time fee: $109 for Brand and Campaign registration
          li Monthly fee: $12/mo for your campaign use case
        p.ml-4.mt-2 We will collect this fee on your behalf using the credit card you have on file.
      v-flex.fee-text(v-else v-show="!isRetryState" xs12 my-0)
        p.ml-4.mb-2 Unlocking texting requires a registration fee by the US mobile carriers - 
          a(href="https://lonewolf.my.site.com/s/article/LD-FAQ-Texting-brand-registration-changes-June-2023-LionDesk" target="_blank") click here to learn more:
        ul.fee-list.ml-4
          li One-time fee: $19 for Brand and Campaign registration
          li Monthly fee: $2/mo for your campaign use case
        p.ml-4.mt-2 We will collect this fee on your behalf using the credit card you have on file.
      v-flex(xs12 v-if="hasRegistrationError" row)
        custom-error-registration(
          :errors="registrationErrors"
          :registrationStatus10dlc="registrationStatus10dlc"
          :registrationCampaignRetries="registrationCampaignRetries"
        )
      v-flex.ml-2(xs12 mt-2)
        v-layout(wrap)
          v-flex(xs6)
            h3.mb-2 Campaign use case description (describe what you will be using this campaign use case for)
            v-textarea.mb-4(
              label="Description"
              v-model="campaignForm.useCase"
              :rules="[rules.required, rules.minLength(40), rules.maxLength(4096)]"
              :outline="true"
              browser-autocomplete="disable"
              required
              rows=3
              no-resize
              messages="Campaign description should be at least 40 characters"
            )
            h3.mt-1.mb-2 Message #1
            v-textarea(
              label="<Write your message or choose from the given samples>"
              v-model="campaignForm.sample1"
              :rules="[rules.required, rules.minLength(20), rules.maxLength(1024)]"
              :outline="true"
              browser-autocomplete="disable"
              required
              rows=3
              no-resize
            )
            h3.mb-2 Message #2
            v-textarea(
              label="<Write your message or choose from the given samples>"
              v-model="campaignForm.sample2"
              :rules="[rules.required, rules.minLength(20), rules.maxLength(1024)]"
              :outline="true"
              browser-autocomplete="disable"
              required
              rows=3
              no-resize
            )
          v-flex.pl-5(xs6)
            h3 Campaign Sample Description
            br
            p.mb-4 Marketing messaging and communications between real estate agents and their contacts database.
            h3 Campaign Sample Messages
            br
            p.mb-4 Provide an example of a message that you will be sending with this campaign use case. This message sample will be used by carriers to help identify your traffic.
            div(v-for="(message, key) in messageSamples" :key="key")
              p {{ message }}
      v-flex.actions(xs12)
        v-btn(
          v-if="!isLoading"
          color="primary"
          outline
          data-cy="back-brand-form"
          @click="back"
        ) Back to Brand
        v-btn(
          v-if="!isLoading"
          color="primary"
          data-cy="a2p-submit"
          :disabled="shouldDisableSubmitButton"
          @click="submit"
        ) Submit
        v-progress-circular(v-if="isLoading" indeterminate color="primary")
</template>
<script>

import { mapMutations, mapActions, mapGetters, mapState } from "vuex";
import CustomErrorRegistration from '@/Core/components/CustomErrorRegistration';
import { STANDARD_RETRY_STATES } from '../config';

import {STANDARD_BRAND} from 'lion-vue-src/config/brand';

export default {
  name: 'FormCampaign',
  props: {
    disableSubmit: {
      type: Boolean,
      default: false,
    },
    validateFormBrand: {
      type: Function,
    },
  },
  components: {
    CustomErrorRegistration
  },
  data() {
    return {
      valid: false,
      campaignForm: {
        useCase: null,
        sample1: null,
        sample2: null,
      },
      messageSamples: {
        example1: "Example #1: Hello {recipient name} this is {agent name}. A new property has hit the market in your area, get more information by responding here. Reply STOP to OPT Out.",
        example2: "Example #2: Hi {recipient name} this is {agent name}. If you're thinking of buying or selling a property, we're here to guide you every step of the way. Let's schedule a consultation to discuss your goals and explore the current market trends.  Reply STOP to OPT Out.",
        example3: "Example #3: Hi {recipient name} this is {agent name}. I'm hosting an open house this Saturday in your desired area. If you'd like to schedule a private showing, reach out to me here. Reply STOP to OPT Out.",
        example4: "Example #4: Hi {recipient name} this is {agent name}. A new listing has become available in your desired area. To learn more or schedule a showing, reply here. Reply STOP to OPT Out.",
      },
      rules: {
        required: value => {
          return !!value || 'This field is required';
        },
        maxLength(max) {
          return value => (value && value.length <= max) || `Max ${max} characters`;
        },
        minLength(min) {
          return value => (value && value.length >= min) || `Min ${min} characters`;
        },
      },
    }
  },
  watch: {
    a2pCampaignForm(campaignData) {
      this.campaignForm = {
        ...this.campaignForm,
        ...campaignData,
      };
    },
  },
  computed: {
    ...mapState('registerCustomerA2p', ['isLoading']),
    ...mapState('registerCustomerA2p', {
      a2pCampaignForm: 'campaignForm'
    }),
    ...mapGetters('registerCustomerA2p', ['isBrandFormValid', 'isStandardRegistration']),
    ...mapState('conversations', [
      'registrationStatus10dlc',
      'registrationStatusData10dlc',
      'registrationBrandRetries',
      'registrationType10dlc',
      'registrationCampaignRetries',
    ]),
    shouldDisableSubmitButton() {
      return this.disableSubmit
        || this.isLoading
        || !this.valid
        || !this.isBrandFormValid;
    },
    hasRegistrationError() {
      return (
        [
          'campaign_tcr_failed',
          'campaign_twilio_failed',
          'campaign_blocked_failed',
          'brand_failed',
          'brand_tcr_failed',
          'a2p_bundle_failed',
          'customer_profile_failed',
        ].includes(this.registrationStatus10dlc) &&
        this.registrationStatusData10dlc &&
        !!this.registrationStatusData10dlc.length
      );
    },
    registrationErrors() {
      let errors = [];
      if (this.hasRegistrationError) {
        errors = errors.concat(this.registrationStatusData10dlc.map(x => x.description));
      }

      if (['brand_tcr_failed'].includes(this.registrationStatus10dlc)) {
        errors = errors.concat(this.brandTCRFailedErrors);
      }

      return errors;
    },
    brandTCRFailedErrors() {
      let errors = [];
      let attempt = ['first', 'second', 'final'];
      let times = 'up to 3';
      if (this.isStandardRegistration) {
        attempt = ['first', 'final'];
        times = '2';
      }
      if ((this.isStandardRegistration && this.registrationBrandRetries < 2) || (!this.isStandardRegistration && this.registrationBrandRetries < 3)) {
        errors = errors.concat([`<strong>This is your ${attempt[this.registrationBrandRetries]} retry attempt</strong>. You may retry ${times} times after your initial identity submission before a new identity verification fee is incurred. Please follow exact form instructions to avoid errors.`]);
      } else {
        errors = errors.concat([`<strong>This submission will restart your identity registration</strong>. US carriers will charge you a verification restart fee to continue.`])
      }
      if (this.registrationBrandRetries > 0) {
        errors = errors.concat([`Need help fixing your registration? <a href='${this.errorMailString}' target='_blank'>Contact support</a> for assistance.`]);
      }
      return errors;
    },
    errorMailString() {
      let errors = '';
      if (this.registrationStatusData10dlc) {
        errors = `${this.registrationStatusData10dlc.map(x => x.description).join('\n\n')}`;
      }
      let attempt = ['', '2nd Retry', '3rd Retry', 'Restart'];
      if (this.isStandardRegistration) {
        attempt = ['', '2nd Retry', 'Restart'];
      }
      var subject = encodeURIComponent(`Texting: Standard registration assistance - ${attempt[this.registrationBrandRetries]}`);
      var body = encodeURIComponent(`My Standard registration is returning this error after multiple attempts:\n\n${errors}`);
      return `mailto:support@liondesk.com?subject=${subject}&body=${body}`
    },
    isStandardRegistration() {
      return this.registrationType10dlc === STANDARD_BRAND;
    },
    isRetryState() {
      return STANDARD_RETRY_STATES.includes(this.registrationStatus10dlc);
    },
  },
  methods: {
    ...mapMutations('registerCustomerA2p', [
      'setCampaignForm',
      'setStep'
    ]),
    ...mapActions('registerCustomerA2p', [
      'sendStandardRegistration',
    ]),
    back() {
      this.setStep({ step: 0 });
    },
    submit() {
      if (!this.validateFormBrand()) {
        this.back();
        return;
      }
      if (!this.$refs.form.validate()) return;

      this.setCampaignForm({ form: this.campaignForm });
      this.sendStandardRegistration();
    },
  },
}
</script>
<style scoped>
h3 {
  font-size: 14px !important;
}

.actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
}

.title-step {
  font-size: 16px !important;
  line-height: 25px;
}

.fee-text {
  background-color: #dee7f1;
  border-radius: 10px;
  font-weight: 500;
  font-size: 16px;
}

.fee-list {
  list-style-type: none;
}
</style>
